<template>
  <div class="media-form popup">
    <HeadSection :title="isEditMode ? `Item #${popupId}` : 'Create new pop-up banner'"
      permissionOfSection="popup-banners" :breadcrumbs="breadcrumbs" :uuid="popupUuid">
      <template #activeSwitch>
        <div class="ml-4">
          <a-switch v-model="form.isActive" />
        </div>
      </template>
      <template #actions>
        <a-col :span="14" class="text-right d-flex justify-content-end">
          <div v-if="isEditMode">
            <a-popconfirm title="Sure to delete?" placement="bottom" @confirm.stop="deletePopup">
              <a-button :loading="loading" type="danger"> Delete </a-button>
            </a-popconfirm>
            <a-button :loading="loading" type="primary" class="ml-4" @click="save">
              Save
            </a-button>
          </div>
          <div v-else class="pt-4">
            <a-button class="ml-4" @click="clearForm" :loading="loading">
              Clear
            </a-button>
            <a-button :loading="loading" type="primary" class="ml-4" @click="save">
              Save
            </a-button>
          </div>
        </a-col>
      </template>
    </HeadSection>
    <MainContainer :loading="mainLoading || userLoading" permissionOfSection="popup-banners">
      <a-form-model :rules="rules" :model="form" ref="form">
        <div class="media-form__main">
          <span class="font-weight-bold font-size-16">General</span>
          <a-row>
            <a-col :span="6">
              <a-form-model-item prop="name" :colon="false">
                <template #label>Name <span class="text-gray-7" style="font-weight: 400;">(for internal
                    usage)</span></template>
                <a-input v-model="form.name" placeholder="Name" size="large" :disabled="loading" />
              </a-form-model-item>
            </a-col>
          </a-row>
          <a-row>
            <a-col :span="6">
              <a-form-model-item prop="contentType" label="Content type">
                <a-select v-model="form.contentType" size="large" :disabled="loading" allow-clear>
                  <template #placeholder><span style="color: #a1a1c2">Select content type</span></template>
                  <a-select-option v-for="contentType in contentTypeOptions" :key="contentType.value"
                    :value="contentType.value">
                    {{ contentType.label }}
                  </a-select-option>
                </a-select>
              </a-form-model-item>
            </a-col>
          </a-row>
          <ActivityPeriods
            ref="activity-periods"
            :periods="popupToEdit?.context.periods"
            :showIndex="false"
            :loading="loading"
            class="mb-2"
            buttonPos="bottom"
            showLabel
            useTimezone
          >
            <template #label>
              Show Period
            </template>
            <template #buttonText>
              + Add new period
            </template>
          </ActivityPeriods>
          <a-row>
            <a-col :span="3">
              <a-form-model-item label="Show limit" prop="showLimit" :colon="false">
                <a-input-number v-model="form.showLimit" :min="1" size="large" :disabled="loading" />
              </a-form-model-item>
            </a-col>
          </a-row>
          <a-row class="daily-limit">
            <a-col :span="3">
              <a-form-model-item label="Daily limit" prop="dailyLimit" :colon="false">
                <a-select v-model="form.dailyLimit" size="large" :disabled="loading" allow-clear>
                  <a-select-option v-for="(_, idx) in [...Array(10)]" :key="'dailyLimit' + idx"
                    :value="idx + 1">
                    {{ idx + 1 }}
                  </a-select-option>
                </a-select>
              </a-form-model-item>
            </a-col>
          </a-row>
          <a-row>
            <a-col :span="6">
             <a-form-model-item prop="segments" :colon="false">
                <template #label>
                  <SegmentsLabel />
                </template>
                <div class="d-flex align-items-center w-full">
                  <SegmentsSelect v-model="form.segments" :segments="segments" :loading="loading"
                    @change="validateSegments" />
                </div>
              </a-form-model-item>
            </a-col>
          </a-row>
          <a-row>
            <a-col :span="6">
              <a-form-model-item label="Screens" prop="sections" :colon="false">
                <a-select v-model="form.sections" mode="multiple" showArrow size="large" allow-clear :disabled="loading">
                  <a-select-option v-for="section in sections" :key="section.value" :value="section.value">
                    {{ section.label }}
                  </a-select-option>
                </a-select>
              </a-form-model-item>
            </a-col>
          </a-row>
          <a-form-model-item prop="apps" required :colon="false" class="d-block">
            <template #label>
              <span class="mr-4">Apps</span>
              <a @click.stop="openAddAppModal" style="color: rgb(0, 123, 255) !important; font-weight: 400">+ Add</a>
            </template>
            <div v-if="form.apps">
              <a-tag style="
                    margin-right: 15px;
                    padding: 1px 8px;
                    border-radius: 2px;
                  " v-for="app in form.apps.slice(0, 5)" :key="app.id">
                {{ app.name }}
                <a-icon v-if="app.platform.toLowerCase() === 'android'" type="android" class="ml-1 text-success" />
                <a-icon v-if="app.platform.toLowerCase() === 'ios'" type="apple" class="ml-1 text-primary" />
                <a-icon v-if="app.platform.toLowerCase() === 'web'" type="global" class="ml-1 text-default" />
              </a-tag>
              <a-tag v-if="form.apps.length > 4"> ... </a-tag>
            </div>
          </a-form-model-item>
          <a-divider class="m-0 mb-3" />
          <div>
            <div class="d-flex flex-row align-items-center justify-content-between mb-2 position-relative">
              <span class="font-weight-bold font-size-16">Media</span>
              <LanguageSelect
                v-model="selectedLanguage"
                :langs="languages"
                :contentFilled="filledLangs"
                :error="formErrorState"
                short-button-label
                :style="{ top: 0 }"
                @copyFromDefault="handleCopyFromDefault"
                @clear="handleClearLang"
              />
            </div>
            <div>
              <div class="media-form__popup mb-3">
                <div class="d-flex flex-column">
                  <span class="mb-2 media-form--title required-label">Image</span>
                  <div>
                    <a-upload list-type="picture-card" class="media-form__popup--uploader" :show-upload-list="false"
                      @change="handleImageUpload" :disabled="loading">
                      <div>
                        <div v-if="selectedImage.url" class="media-form__popup--image">
                          <img :src="selectedImage.url" />
                        </div>
                        <template v-else>
                          <a-icon :type="selectedImage.loading ? 'loading' : 'inbox'" class="text-primary"
                            style="font-size: 48px" />
                          <div>
                            Upload<br />
                            <div class="text-gray-7" style="font-size: 11px;">
                              File size 1400 x 2200 px
                            </div>
                          </div>
                        </template>
                      </div>
                    </a-upload>
                  </div>
                </div>
              </div>
              <a-row class="mb-4">
                <a-col :span="7">
                  <div class="d-flex flex-column">
                    <div>
                      <span class="text-bold required-label mr-2">Banner theme</span>
                      <a-tooltip slot="suffix" title="Select text color depending on the image background">
                        <a-icon type="info-circle" class="text-primary" />
                      </a-tooltip>
                    </div>
                    <a-radio-group name="radioGroup" v-model="selectedInfo.theme" class="mt-2">
                      <a-radio value="light"> Light </a-radio>
                      <a-radio value="dark"> Dark </a-radio>
                    </a-radio-group>
                  </div>
                </a-col>
              </a-row>
              <div>
                <div>
                  <div class="media-form__banner--redirect__dialogue--text" style="max-width: 650px;">
                    <span class="text-bold required">Title</span>
                    <quill-editor v-model="selectedInfo.title" @change="($event) => validateQuillLength($event, 100)"
                      :options="quillEditorOptions" style="width: 650px;" />
                    <div class="media-form__text-count">{{stripHtml(selectedInfo.title).length }} / 100</div>
                  </div>
                  <div class="media-form__banner--redirect__dialogue--text" style="max-width: 650px;">
                    <span class="text-bold required">Text area</span>
                    <quill-editor v-model="selectedInfo.description"
                      @change="($event) => validateQuillLength($event, 250)" :options="quillEditorOptions"
                      style="width: 650px;" />
                    <div class="media-form__text-count">{{stripHtml(selectedInfo.description).length }} / 250</div>
                  </div>
                </div>
              </div>
            </div>
            <div class="d-flex flex-column mb-2">
              <div class="mb-3">
                <span class="font-weight-bold font-size-14 mr-3">Redirect</span>
                <a-switch v-model="selectedRedirect.isRedirectAvailable" @change="handleRedirectSwitch" />
              </div>
              <div v-if="selectedRedirect.isRedirectAvailable">
                <div class="d-flex flex-column redirect-list">
                  <div class="">
                    <a-row class="mb-3">
                      <a-col :span="3" class="mr-4">
                        <a-form-model-item :colon="false">
                          <template #label><span class="required-label">Name</span></template>
                          <a-input placeholder="Name" :disabled="loading" v-model="selectedRedirect.buttonName"
                            size="large" />
                        </a-form-model-item>
                      </a-col>
                      <a-col :span="3" class="mr-4">
                        <a-form-model-item prop="buttonColor" :colon="false">
                           <template #label><span class="required-label">Button color</span></template>
                          <a-input placeholder="#112233" :disabled="loading" v-model="selectedRedirect.buttonColor"
                            size="large" />
                        </a-form-model-item>
                      </a-col>
                      <a-col :span="3">
                        <a-form-model-item prop="fontColor" :colon="false">
                          <template #label><span class="required-label">Font color</span></template>
                          <a-input placeholder="#112233" :disabled="loading" v-model="selectedRedirect.fontColor"
                            size="large" />
                        </a-form-model-item>
                      </a-col>
                    </a-row>
                    <a-row>
                      <a-col :span="3" class="mr-4">
                        <a-form-model-item :colon="false">
                          <template #label><span class="required-label">Link type</span></template>
                          <a-select v-model="selectedRedirect.linkType" size="large" :disabled="loading" allow-clear
                            @change="handleLinkTypeChange">
                            <template #placeholder><span style="color: #a1a1c2">Select link type</span></template>
                            <a-select-option value="custom"> Custom </a-select-option>
                            <a-select-option value="sport"> Sport </a-select-option>
                            <a-select-option value="match"> Match </a-select-option>
                          </a-select>
                        </a-form-model-item>
                      </a-col>
                      <a-col :span="7">
                        <div v-if="selectedRedirect.linkType === 'custom'">
                          <a-form-model-item :colon="false">
                            <template #label>
                              <span class="required-label">Link</span>
                              <a-button @click="openAddLinkTemplatesModal" size="small" type="link"
                                style="padding-left: 8px !important; height: 15px; line-height: 18px;">Save
                                as a
                                template</a-button>
                            </template>
                            <a-select class="media-form__custom-select" :value="selectedRedirect.link"
                              :disabled="loading" mode="tags" @change="handleTemplateChange" @inputKeydown="(e) => handleLinkTypeChange(e)" optionLabelProp="label"
                              size="large">
                              <template #placeholder><span
                                  style="color: #a1a1c2">https://www.olimp.bet/line/1/5956496/78487617</span></template>
                              <a-select-option v-for="linkTemplate in filteredLinkTemplates" :key="String(linkTemplate.id)"
                                :value="String(linkTemplate.url)" :label="linkTemplate.url">
                                <div class="d-flex justify-content-between align-items-center">
                                  <div class="link-container">
                                    <span class="link-name">{{ linkTemplate.name }}</span>
                                    <span class="link-url">{{ linkTemplate.url || '' }}</span>
                                  </div>
                                  <a-popconfirm overlayClassName="deleteTemplateOverlay" :disabled="false"
                                    :getPopupContainer="getPopupContainer" title="Sure to delete this template?"
                                    @confirm="(e) => deleteLinkTemplate(e, linkTemplate.id)">
                                    <a-icon class="text-danger delete-icon" type="delete" @click.stop />
                                  </a-popconfirm>
                                </div>
                              </a-select-option>
                            </a-select>
                          </a-form-model-item>
                        </div>
                        <div v-if="selectedRedirect.linkType === 'match'">
                          <a-form-model-item :colon="false" label="Match link">
                            <a-input placeholder="https://www.olimp.bet/line/1/5956496/78487617" :disabled="loading"
                              v-model="selectedRedirect.link" size="large" />
                          </a-form-model-item>
                        </div>
                        <div v-if="selectedRedirect.linkType === 'sport'">
                          <a-form-model-item :colon="false" label="Sport">
                            <a-select v-model="selectedRedirect.sportId" size="large" :disabled="loading" allow-clear>
                              <template #placeholder><span style="color: #a1a1c2;">Select sport</span></template>
                              <a-select-option v-for="sport in sports" :key="sport.sport_id">
                                {{ sport.name }}
                              </a-select-option>
                            </a-select>
                          </a-form-model-item>
                        </div>
                      </a-col>
                    </a-row>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </a-form-model>
      <div v-if="!errors.oneFullPopup" style="color: red; margin-top: 8px;">At least one language should have image,
        title and description</div>
      <div v-if="!errors.noInvalidPopup" style="color: red; margin-top: 8px">Can't create pop-up banner with image but
        without title & text area and vice versa</div>
      <div v-if="!errors.isRedirectValid" style="color: red; margin-top: 8px">Redirect must have name, button color, font color, link type and
        link / sport itself</div>
      <div v-if="!errors.redirectColorLength" style="color: red; margin-top: 8px">Redirect color inputs can't be longer
        than 7 chars</div>
    </MainContainer>
    <a-modal v-model="addAppsModal" title="Add new apps" :footer="null" width="700px" :destroyOnClose="true">
      <AppsModal @selectApps="changeSelectedApps" @close="closeAddAppsModal" :modalAppsData="modalAppsData" />
    </a-modal>
    <a-modal v-model="addLinkTemplateModal" width="395px" :destroyOnClose="true" @ok="handleAddTemplatesModal">
      <template #title><span class="modal-title">Add new template</span></template>
      <a-form-model :model="addTemplateForm" ref="templateForm" :rules="templateRules">
        <a-form-model-item label="Name" prop="name" :colon="false">
          <a-input placeholder="Name" :disabled="loading" size="large" v-model="addTemplateForm.name"></a-input>
        </a-form-model-item>
        <a-form-model-item label="Link" prop="link" :colon="false">
          <a-input placeholder="https://www.olimp.bet/line/1/5956496/7848761" :disabled="loading" size="large"
            v-model="addTemplateForm.link"></a-input>
        </a-form-model-item>
      </a-form-model>
    </a-modal>
  </div>
</template>

<script>
import HeadSection from '@/components/main/headSection'
import MainContainer from '@/components/main/mainContainer'
import AppsModal from '@/components/custom/modals/appsModal.vue'
import ActivityPeriods from '@/components/custom/ActivityPeriods.vue'
import LanguageSelect from '@/components/custom/languageSelect.vue'
import { quillEditor } from 'vue-quill-editor'
import SegmentsSelect from '@/components/custom/segments/segmentsSelect.vue'
import SegmentsLabel from '@/components/custom/segments/segmentsLabel.vue'

import ViewscreenService from '@/services/api/apiViewscreenService'
import MediaService from '@/services/api/apiMediaService'
import apiClient from '@/services/axios'
import { mapState } from 'vuex'

import { returnToLastList } from '@/services/utils/routerHelpers'
import { DEFAULT_LANG } from '@/constants/lang'
import { POPUP_CONTENT_FORM_FIELDS, defaultPopupImg, defaultPopupInfo, defaultPopupRedirect } from '@/constants/media/popups'

const initialForm = {
  name: '',
  contentType: undefined,
  segments: [],
  isActive: true,
  showLimit: null,
  dailyLimit: null,
  sections: [],
  apps: [],
}

const isContentEqual = (content, template) => {
  if (!content || !template) return content === template

  const imgEqual =
    content.popupImage?.url === (template.popupImage?.url ?? '') &&
    content.popupImage?.file === (template.popupImage?.file ?? null) &&
    content.popupImage?.loading === (template.popupImage?.loading ?? false)

  if (!imgEqual) return false

  const infoEqual =
    content.popupInfo?.title === (template.popupInfo?.title ?? '') &&
    content.popupInfo?.description === (template.popupInfo?.description ?? '') &&
    content.popupInfo?.theme === (template.popupInfo?.theme ?? 'light')

  if (!infoEqual) return false

  const redirectEqual =
    content.popupRedirect?.isRedirectAvailable === (template.popupRedirect?.isRedirectAvailable ?? false)

  return redirectEqual
}

const deepClone = (obj) => {
  return JSON.parse(JSON.stringify(obj))
}

function getImageDimensions(file) {
  return new Promise((resolve) => {
    const img = document.createElement('img')
    img.src = URL.createObjectURL(file)
    img.onload = () => {
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
      })
    }
  })
}

function getBase64(img) {
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.addEventListener('load', () => resolve(reader.result))
    reader.readAsDataURL(img)
  })
}

export default {
  name: 'popupForm',
  components: { AppsModal, SegmentsSelect, SegmentsLabel, HeadSection, MainContainer, ActivityPeriods, quillEditor, LanguageSelect },
  data() {
    return {
      form: { ...initialForm },
      content: {
        ru: {
          popupImage: {
            ...defaultPopupImg,
          },
          popupInfo: {
            ...defaultPopupInfo,
          },
          popupRedirect: {
            ...defaultPopupRedirect,
          },
        },
      },
      errors: {
        oneFullPopup: true,
        noInvalidPopup: true,
        isRedirectValid: true,
        redirectColorLength: true,
      },
      sections: [
        { label: 'Live', value: 1 },
        { label: 'Line', value: 2 },
        { label: 'Promo', value: 3 },
        { label: 'Main Web', value: 4 },
        { label: 'Registration', value: 5 },
        { label: 'Side menu', value: 6 },
        { label: 'Bottom menu', value: 7 },
      ],
      contentTypeOptions: [
        { label: 'Partners', value: 'Partners' },
        { label: 'Promotion', value: 'Promotion' },
        { label: 'Editorial', value: 'Editorial' },
        { label: 'SMM', value: 'SMM' },
        { label: 'Product', value: 'Product' },
        { label: 'Special project', value: 'Special project' },
      ],
      loading: false,
      mainLoading: false,
      validationResult: true,
      addAppsModal: false,
      addUserModal: false,
      editSegmentModal: false,
      languages: ['ru'],
      selectedLanguage: 'ru',
      linkTemplates: [],
      sports: [],
      addTemplateForm: {
        name: '',
        link: '',
      },
      segments: null,
      isRedirectAvailable: false,
      checkAllLangs: false,
      addLinkTemplateModal: false,
      popupToEdit: null,
      rules: {
        name: [
          {
            required: true,
            message: 'Pop-up banner name is required',
            trigger: 'blur',
          },
        ],
        segments: [
          {
            type: 'array',
            required: true,
            message: 'Please select at least one segment',
            trigger: 'change',
          },
        ],
        showLimit: [
          {
            required: true,
            message: 'Show limit is required',
            trigger: 'blur',
          },
        ],
        dailyLimit: [
          {
            required: true,
            message: 'Daily limit is required',
            trigger: 'blur',
          },
        ],
        sections: [
          {
            type: 'array',
            required: true,
            message: 'Please select at least one section',
            trigger: 'change',
          },
        ],
        apps: [
          {
            type: 'array',
            required: true,
            message: 'Applications are required',
            trigger: 'change',
          },
        ],
      },
      templateRules: {
        name: [
          {
            required: true,
            message: 'This field is required',
            trigger: 'blur',
          },
        ],
        link: [
          {
            required: true,
            message: 'This field is required',
            trigger: 'blur',
          },
        ],
      },
      modalAppsData: null,
      quillEditorOptions: {
        placeholder: 'Input description',
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline'], // toggled buttons
            ['blockquote'],

            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ indent: '-1' }, { indent: '+1' }], // outdent/indent
            [{ direction: 'rtl' }], // text direction
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            [{ color: [] }, { background: [] }], // dropdown with defaults from theme
            [{ align: [] }],

            ['link'],
          ],
        },
      },
    }
  },
  computed: {
    ...mapState(['user']),
    userLoading() {
      return this.$store.state?.user?.loading || false
    },
    isEditMode() {
      return this.$route.path.split('/').includes('edit')
    },
    routeTitle() {
      return 'Pop-up banners'
    },
    popupTitle() {
      return this.isEditMode ? 'Edit pop-up banner' : 'New pop-up banner'
    },
    selectedContent() {
      return this.content[this.selectedLanguage]
    },
    selectedInfo() {
      return this.selectedContent.popupInfo
    },
    selectedRedirect() {
      return this.selectedContent.popupRedirect
    },
    selectedImage () {
      return this.selectedContent.popupImage
    },
    isDefaultContentFilled() {
      const defaultLang = 'ru'
      const { title, description } = this.content[defaultLang].popupInfo
      return this.content[defaultLang].popupImage.id && title?.length && description?.length
    },
    breadcrumbs() {
      const defaultRootLink = '/media/popup-banners'
      const lastSavedUrl = localStorage?.getItem('lastFilterListUrl')
      const rootLink = lastSavedUrl?.startsWith(defaultRootLink) ? lastSavedUrl : defaultRootLink

      return [
        { name: 'Media', link: rootLink },
        { name: this.isEditMode ? 'Edit pop-up banner' : 'New pop-up banner', link: undefined },
      ]
    },
    filteredLinkTemplates() {
      const uniqueTemplates = new Set()
      return this.linkTemplates.filter(template => {
        const url = template.url.trim()
        if (!url || uniqueTemplates.has(url)) {
          return false
        }
        uniqueTemplates.add(url)
        return true
      })
    },
    popupId() {
      if (!this.isEditMode) {
        return null
      }
      return this.$route.params.id
    },
    popupUuid() {
      if (!this.popupToEdit) {
        return null
      }
      return this.popupToEdit.context.uuid
    },
    isInitialForm() {
      return isContentEqual(this.selectedContent, POPUP_CONTENT_FORM_FIELDS)
    },
    formErrorState() {
      if (this.isInitialForm) {
        return null
      }
      return this.validationResult ? null : 'Form is not filled correctly'
    },
    filledLangs() {
      return this.languages.reduce((obj, { name }) => {
        obj[name] = !isContentEqual(this.content[name], POPUP_CONTENT_FORM_FIELDS)
        return obj
      }, {})
    },
  },
  watch: {
    selectedContent: {
      deep: true,
      handler() {
        if (this.isInitialForm) {
          return
        }
        this.$nextTick(() => this.validateFormState())
      },
    },
    selectedLanguage() {
      if (this.isInitialForm) {
        return
      }
      this.validateFormState()
    },
  },
  async created() {
    this.mainLoading = true

    await this.fetchLocalizations()
    this.fetchSports()
    this.fetchLinkTemplates()
    this.getSegments()

    if (this.isEditMode) {
      this.popupToEdit = await this.getPopup()
      this.fillForm()
    }

    if (!this.popupToEdit) {
      this.selectedLanguage = this.languages[0].name
      this.initializeContent()
    }
    this.mainLoading = false
  },
  methods: {
    getPopupContainer() {
      return document.body
    },
    async save() {
      const isValid = await this.validateForm()
      const isPeriodsValidate = await this.$refs['activity-periods'].validate()
      if (!isValid || !isPeriodsValidate) {
        return false
      }
      if (!this.isEditMode) {
        return this.createPopup()
      }
      this.updatePopup()
    },
    async validateForm() {
      const isValid = await new Promise((resolve) => {
        this.$refs.form.validate(resolve)
      })

      let isAllEmpty = true
      let isOneInvalid = false
      let allRedirectsValid = true
      let allRedirectsColorValid = true

      for (const lang in this.content) {
        const currentLang = this.content[lang]
        const isImgEmpty = !currentLang.popupImage.url
        const isTitleEmpty = !currentLang.popupInfo.title || currentLang.popupInfo.title.trim() === ''
        const isDescEmpty = !currentLang.popupInfo.description || currentLang.popupInfo.description.trim() === ''

        const isCurrentLangEmpty = isImgEmpty && isTitleEmpty && isDescEmpty
        const isCurrentLangValid = !isImgEmpty && !isTitleEmpty && !isDescEmpty

        if (isCurrentLangValid) {
          isAllEmpty = false
        }

        if (!isCurrentLangEmpty && !isCurrentLangValid) {
          isOneInvalid = true
        }

        if (currentLang.popupRedirect.isRedirectAvailable) {
          const { buttonColor, buttonName, fontColor, linkType, sportId, link } = currentLang.popupRedirect

          const isMissingButton = !buttonName || !linkType || !buttonColor || !fontColor
          const isCustomMissing = linkType === 'custom' && (!link || link.length === 0)
          const isSportMissing = linkType === 'sport' && (sportId === undefined || sportId === null || sportId === '')
          const isLinkMissing = linkType !== 'sport' && (!link || link.length === 0)

          if (isMissingButton || isCustomMissing || isSportMissing || isLinkMissing) {
            allRedirectsValid = false
          }
          if (buttonColor.length > 7 || fontColor.length > 7) {
            allRedirectsColorValid = false
          }
        }
      }

      const errors = {
        oneFullPopup: !isAllEmpty,
        noInvalidPopup: !isOneInvalid,
        isRedirectValid: allRedirectsValid,
        redirectColorLength: allRedirectsColorValid,
      }
      this.errors = { ...errors }
      if (!isValid || Object.values(this.errors).includes(false)) {
        return false
      }
      return true
    },
    async validateUploadedImage(file) {
      const isImage = file.type.split('/')[0] === 'image'
      const fileExtension = file.type.split('/')[1]

      if (!isImage) {
        this.$notification.error({
          message: 'You can only upload WEBP, PNG or JPEG file!',
        })
        return false
      }

      const isLt10M = file.size / 1024 / 1024 < 10

      if (!isLt10M) {
        this.$notification.error({
          message: 'Pop-up image must be smaller than 10MB!',
        })
        return false
      }

      if (isImage) {
        const { width, height } = await getImageDimensions(file)
        if (width !== 1400 || height !== 2200) {
          this.$notification.error({
            message: 'Pop-up image must be 1400 x 2200px!',
          })
          return false
        }
        if (fileExtension !== 'png' && fileExtension !== 'webp' && fileExtension !== 'jpeg') {
          this.$notification.error({
            message: 'Pop-up image must have WEBP, PNG or JPEG extension!',
          })
          return false
        }
      }

      return file
    },
    handleCopyFromDefault() {
      if (!this.content[DEFAULT_LANG] || !this.content[this.selectedLanguage]) {
        return
      }
      const defaultForm = this.content[DEFAULT_LANG]
      this.content[this.selectedLanguage] = JSON.parse(JSON.stringify(defaultForm))
    },
    handleClearLang(lang) {
      this.content[lang] = {
        popupImage: { ...defaultPopupImg },
        popupInfo: { ...defaultPopupInfo },
        popupRedirect: { ...defaultPopupRedirect },
      }
      this.$refs.form.clearValidate()
    },
    initializeContent() {
      this.content = this.languages.reduce((acc, language) => {
        return {
          ...acc,
          [language.name]: {
            popupImage: { ...defaultPopupImg },
            popupInfo: { ...defaultPopupInfo },
            popupRedirect: { ...defaultPopupRedirect },
          },
        }
      }, {})
    },
    handleTemplateChange(newTemplate) {
      const filtered = (newTemplate || []).filter(template => template !== '' && template !== undefined)
      const last = filtered[filtered.length - 1] || []
      const foundTemplate = this.linkTemplates.find(link => link.url === last)
      const templateName = foundTemplate ? foundTemplate.name : ''
      this.selectedRedirect.link = last
      this.selectedRedirect.linkName = templateName
    },
    handleLinkTypeChange(event) {
      if (event && event.key !== undefined) {
        if (event.key.length !== 1) return
      }
      Object.assign(this.selectedRedirect, { link: [], linkName: '', sportId: '' })
    },
    handleLangChange(selectedLangKey) {
      if (this.loading) {
        return
      }
      this.selectedLanguage = selectedLangKey
    },
    handleRedirectSwitch() {
      const { isRedirectAvailable, ...restDefaults } = defaultPopupRedirect
      this.content[this.selectedLanguage].popupRedirect = {
        ...restDefaults,
        isRedirectAvailable: this.selectedRedirect.isRedirectAvailable,
      }
    },
    async handleImageUpload(event) {
      if (event.file.status === 'uploading') {
        return
      }

      const file = event.file

      const isFileValid = await this.validateUploadedImage(
        event.file.originFileObj,
      )

      if (!isFileValid) {
        return
      }

      const fileId = file.uid

      this.content[this.selectedLanguage].popupImage = {
        ...this.selectedImage,
        loading: true,
        id: fileId,
        file,
      }

      const formData = new FormData()

      formData.append('cover', file.originFileObj)

      try {
        const response = await MediaService.uploadImage(file)
        const previewId = response.data?.data?.id
        if (!previewId) {
          const errorMessage =
          response.data?.error?.message ||
            'Something went wrong while uploading pop-up image'
          this.content[this.selectedLanguage].popupImage = {
            ...defaultPopupImg,
          }
          this.$notification.error({
            message: errorMessage,
          })
        }
        this.content[this.selectedLanguage].popupImage.id = previewId
        this.content[this.selectedLanguage].popupImage.url = await getBase64(
          event.file.originFileObj,
        )
        this.content[this.selectedLanguage].popupImage.loading = false
      } catch (e) {
        const errorMessage =
          e.response.data?.error?.message ||
          'Something went wrong while uploading the preview'
        this.content[this.selectedLanguage].popupImage = {
          ...defaultPopupImg,
        }
        this.$notification.error({
          message: errorMessage,
        })
      }
    },
    validateQuillLength(e, maxLength) {
      const text = e.text.trim() ?? ''

      if (maxLength && maxLength < text.length) {
        e.quill.setText(text.substring(0, maxLength))
      }
    },
    stripHtml(htmlString) {
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = htmlString

      return tempDiv.textContent || tempDiv.innerText || ''
    },
    fillForm() {
      const { name, content_type, limit, daily_limit, segments, applications, displays, is_active, images } = this.popupToEdit.context
      this.form.name = name
      this.form.contentType = content_type
      this.form.apps = applications
      this.modalAppsData = applications
      this.form.showLimit = limit
      this.form.dailyLimit = daily_limit
      this.form.isActive = is_active
      this.form.sections = displays.map((display) => {
        return display.id
      })
      this.form.segments = segments.map(segment => segment.id)
      const popupLanguages = Object.keys(images)
      this.selectedLanguage = popupLanguages[0]
      const popupContent = popupLanguages.reduce((acc, language) => {
        const popup = images[language]
        const redirect = popup.image.redirect || {}
        const content = {
          popupInfo: {
            title: popup.title,
            description: popup.description,
            theme: popup.theme,
          },
        }
        const popupImage = {
          ...(popup.image.id && { id: popup.image.id }),
          ...(popup.image.path && { url: popup.image.path }),
        }
        if (Object.keys(popupImage).length) {
          content.popupImage = popupImage
        }

        if (Object.keys(redirect).length) {
          const linkType = redirect.link?.type || 'custom'
          const button = {
            buttonName: popup.image?.button?.name || '',
            buttonColor: popup.image?.button?.button_color || '',
            fontColor: popup.image?.button?.font_color || '',
          }
          const popupRedirect = {
            isRedirectAvailable: true,
            linkType: linkType,
            link: linkType === 'sport' ? '' : redirect.link.url,
            linkName: linkType === 'custom' ? redirect.link?.name || '' : '',
            sportId: linkType === 'sport' ? redirect.link?.sport_id || '' : '',
            ...(Object.keys(button).length && { ...button }),
          }
          content.popupRedirect = popupRedirect
        } else {
          content.popupRedirect = deepClone(defaultPopupRedirect)
        }
        acc[language] = content
        return acc
      }, {})
      const defaultContent = this.languages.reduce((acc, language) => {
        return {
          ...acc,
          [language.name]: {
            popupImage: { ...deepClone(defaultPopupImg) },
            popupInfo: { ...deepClone(defaultPopupInfo) },
            popupRedirect: { ...deepClone(defaultPopupRedirect) },
          },
        }
      }, {})

      this.content = {
        ...defaultContent,
        ...popupContent,
      }
    },
    getPopupContentByLanguage() {
      return Object.entries(this.content).reduce((acc, [language, content]) => {
        const { theme, title, description } = content.popupInfo
        const popupInfo = {
          title: title,
          description: description,
          id: content.popupImage.id,
          theme: theme,
        }
        const redirect = content.popupRedirect
        if (redirect.isRedirectAvailable) {
          const { buttonName, buttonColor, fontColor, linkType, linkName, link, sportId } = redirect

          popupInfo.redirect = {
            type: 'link',
            link: {
              type: linkType,
              ...(link.length && { url: link || '' }),
              ...(linkType === 'custom' && { name: linkName }),
              ...(linkType === 'sport' && { sport_id: sportId }),
            },
          }
          const button = {
            ...(buttonName?.length && { name: buttonName }),
            ...(buttonColor?.length && { button_color: buttonColor }),
            ...(fontColor?.length && { font_color: fontColor }),
          }

          if (Object.keys(button).length) {
            popupInfo.button = button
          }
        }
        if (title !== '' && description !== '') {
          return {
            ...acc,
            [language]: popupInfo,
          }
        }
        return acc
      }, {})
    },
    getPopupPayload() {
      const popupPayload = {
        name: this.form.name,
        ...(this.form.contentType !== undefined && { content_type: this.form.contentType }),
        periods: this.$refs['activity-periods'].getPeriods(),
        segments: this.form.segments,
        limit: this.form.showLimit,
        daily_limit: this.form.dailyLimit,
        displays: this.form.sections,
        is_active: this.form.isActive,
        applications: this.getApplications(),
        images: this.getPopupContentByLanguage(),
      }
      return popupPayload
    },
    clearForm() {
      this.form = { ...initialForm }
      this.initializeContent()
    },
    getApplications() {
      return this.form.apps.map((app) => {
        return {
          id: app.application_id,
          ...(app.min_app_version !== '' && { min_app_version: app.min_app_version }),
          ...(app.max_app_version !== '' && { max_app_version: app.max_app_version }),
        }
      })
    },
    async validateSegments() {
      const isValid = await new Promise((resolve) => {
        this.$refs.form.validateField('segments', resolve)
      })
      return isValid
    },
    async validateApps() {
      const isValid = await new Promise((resolve) => {
        this.$refs.form.validateField('apps', resolve)
      })
      return isValid
    },
    openAddAppModal() {
      if (this.loading) {
        return
      }
      this.addAppsModal = true
    },
    closeAddAppsModal() {
      this.addAppsModal = false
    },
    handleAddUser() {
      this.closeUserModal()
      this.openEditSegmentModal()
    },
    closeUserModal() {
      this.addUserModal = false
    },
    openEditSegmentModal() {
      if (this.loading) {
        return
      }
      this.editSegmentModal = true
    },
    closeEditSegmentModal() {
      this.editSegmentModal = false
    },
    openAddUserModal() {
      this.closeEditSegmentModal()
      this.addUserModal = true
    },
    changeSelectedApps(apps) {
      this.closeAddAppsModal()
      this.form.apps = apps
      this.modalAppsData = apps
      this.validateApps()
    },
    openAddLinkTemplatesModal() {
      if (this.loading) {
        return
      }
      this.addLinkTemplateModal = true
    },
    closeAddLinkTemplatesModal() {
      this.addLinkTemplateModal = false
    },
    async deleteLinkTemplate(event, templateId, deletedlink) {
      try {
        const linkResponse = await MediaService.deleteLink(templateId)

        if (!linkResponse || linkResponse.success === false) {
          this.$notification.error({
            message: 'Error while deleting link',
            description: linkResponse?.data.error || 'Unknown error',
          })
          return
        }
        this.$notification.success({
          message: 'Link deleted successfully',
        })
        this.linkTemplates = this.linkTemplates.filter(link => link.id !== templateId)
        if (this.selectedLinkRedirect.link === deletedlink) {
          this.selectedLinkRedirect.link = []
        }
        if (this.selectedDialogueButton.link === deletedlink) {
          this.selectedDialogueButton.link = []
        }
      } catch (e) {
        this.$notification.error({
          message: 'Error while deleting link',
          description: e.response.data.error.message,
        })
      }
      event.stopPropagation()
    },
    redirectToMedia() {
      returnToLastList(this.$router, '/media/popup-banners')
    },
    validateFormState() {
      if (!this.$refs.form) return

      this.$refs.form.validate((valid) => {
        this.validationResult = valid
      })
    },
    async getPopup() {
      try {
        this.loading = true
        const popupResponse = await MediaService.getPopup(this.$route.params.id)
        this.loading = false
        return popupResponse.data.data
      } catch (err) {
        this.$notification.error({
          message: err.response.data.error.message,
        })
        this.loading = false
      }
    },
    async createPopup() {
      try {
        this.mainLoading = true

        const popupPayload = this.getPopupPayload()
        const popupResponse = await MediaService.createPopup(popupPayload)
        if (popupResponse.data.data) {
          this.$notification.success({
            message: 'Pop up banner successfully created',
          })
          this.redirectToMedia()
        }
        if (popupResponse?.data?.error || popupResponse.data.success === false || popupResponse.data.error) {
          this.$notification.error({
            message: 'Error while creating popup',
            description: popupResponse?.data?.error?.message || '',
          })
          this.mainLoading = false
          return
        }
      } catch (err) {
        const errorMessage =
        err.response?.data?.error?.message || err.message || 'An unexpected error occurred'

        this.$notification.error({
          message: errorMessage,
        })
        this.mainLoading = false
      } finally {
        this.mainLoading = false
      }
    },
    async updatePopup() {
      try {
        this.mainLoading = true

        const popupPayload = this.getPopupPayload()
        const popupResponse = await MediaService.updatePopup(this.popupId, popupPayload)
        if (popupResponse.data.data) {
          this.$notification.success({
            message: 'Pop up successfully updated',
          })
          this.redirectToMedia()
        }
        if (popupResponse?.data?.error || popupResponse.data.success === false || popupResponse.data.error) {
          this.$notification.error({
            message: 'Error while updating popup',
            description: popupResponse?.data?.error?.message || '',
          })
          this.loading = false
          this.mainLoading = false
          return
        }
      } catch (err) {
        const errorMessage =
        err.response?.data?.error?.message || err.message || 'An unexpected error occurred'

        this.$notification.error({
          message: errorMessage,
        })
        this.loading = false
        this.mainLoading = false
      } finally {
        this.loading = false
        this.mainLoading = false
      }
    },
    async deletePopup() {
      try {
        this.mainLoading = false
        const popupResponse = await MediaService.deletePopup(this.popupId)
        if (popupResponse.data.data) {
          this.$notification.success({
            message: 'Pop-up successfully deleted',
          })
          this.redirectToMedia()
        }
        if (popupResponse?.data?.error || popupResponse.data.success === false || popupResponse.data.error) {
          this.$notification.error({
            message: 'Error while deleting popup',
            description: popupResponse?.data?.error?.message || '',
          })
          this.mainLoading = false
          return
        }
      } catch (err) {
        const errorMessage =
        err.response?.data?.error?.message || err.message || 'An unexpected error occurred'

        this.$notification.error({
          message: errorMessage,
        })
        this.mainLoading = false
      } finally {
        this.mainLoading = false
      }
    },
    async handleAddTemplatesModal() {
      try {
        this.loading = true
        const templateResponse = await apiClient.post(
          '/admin/links',
          this.addTemplateForm,
        )
        this.linkTemplates.push(templateResponse.data.data)
        this.closeAddLinkTemplatesModal()
        this.$refs.templateForm.resetFields()
        if (templateResponse.data.error) {
          this.loading = false
          this.$notification.error({
            message: 'Error while adding templates',
            description: templateResponse.data.error.message,
          })
          return
        }
      } catch (e) {
        this.$notification.error({
          message: 'Error while adding templates',
          description: e.response.data.error.message,
        })
        this.loading = false
      }
      this.loading = false
    },
    async fetchLocalizations() {
      this.languagesLoading = true
      const url = '/admin/localizations'
      try {
        const response = await apiClient.get(url)
        this.languages = response.data.data
      } catch (error) {
        console.log('Error while trying to get locales', error)
      } finally {
        this.languagesLoading = false
      }
    },
    async fetchSports() {
      this.loading = true
      const url = '/admin/sports?results=1000'
      try {
        const response = await apiClient.get(url)
        this.sports = response.data.data
      } catch (error) {
        console.log('Error while trying to get sports', error)
      } finally {
        this.loading = false
      }
    },
    async fetchLinkTemplates() {
      this.loading = true
      const url = '/admin/links?type=custom&results=1000'
      try {
        const response = await apiClient.get(url)
        this.linkTemplates = response.data.data
      } catch (error) {
        console.log('Error while trying to get templates', error)
      } finally {
        this.loading = false
      }
    },
    async getSegments() {
      this.loading = true
      try {
        const response = await ViewscreenService.getSegmentatorSegments()
        if (response.data.data) {
          this.segments = response.data.data
        }
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" src="../../style.module.scss" />
